import { createFileRoute, Link } from "@tanstack/react-router";
import { LegalLayout } from "@/components/legal-layout";

export const Route = createFileRoute("/cookies")({
  component: CookiesPage,
});

function CookiesPage() {
  return (
    <LegalLayout title="Cookie Policy" lastUpdated="December 24, 2025">
      <section className="space-y-6">
        <div>
          <h2 className="text-2xl font-semibold mb-4">1. What Are Cookies?</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Cookies are small text files that are placed on your device
            (computer, smartphone, or tablet) when you visit a website. They are
            widely used to make websites work more efficiently and provide
            information to website owners.
          </p>
          <p className="text-muted-foreground leading-relaxed">
            Cookies can be "session cookies" (deleted when you close your
            browser) or "persistent cookies" (stored for a set period or until
            you delete them).
          </p>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">2. How We Use Cookies</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            We use cookies for the following purposes:
          </p>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
            <li>
              <strong>Authentication:</strong> To keep you logged in as you
              navigate our platform
            </li>
            <li>
              <strong>Security:</strong> To detect and prevent fraud and abuse
            </li>
            <li>
              <strong>Functionality:</strong> To remember your preferences and
              settings
            </li>
          </ul>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">3. Cookies We Use</h2>

          <h3 className="text-xl font-semibold mb-3 mt-6">
            3.1 Essential Cookies
          </h3>
          <div className="bg-muted/50 border border-border rounded-2xl p-6 mb-6">
            <p className="mb-4">
              <strong>Purpose:</strong> Authentication and session management
            </p>
            <p className="mb-2">
              <strong>Cookie Name:</strong> Better Auth session cookies
            </p>
            <p className="mb-2">
              <strong>Type:</strong> HTTP-only, Secure
            </p>
            <p className="mb-2">
              <strong>Duration:</strong> 30 days (session-based)
            </p>
            <p className="mb-4">
              <strong>Data Stored:</strong> Session identifier (encrypted)
            </p>
            <p className="text-sm text-muted-foreground">
              These cookies are strictly necessary for the service to function.
              They enable you to log in, maintain your session, and access
              authenticated features. Under GDPR, consent is not required for
              essential cookies.
            </p>
          </div>

          <h3 className="text-xl font-semibold mb-3 mt-6">
            3.2 Third-Party Cookies
          </h3>

          <div className="bg-muted/50 border border-border rounded-2xl p-6 mb-6">
            <p className="mb-4">
              <strong>Stripe Payment Cookies</strong>
            </p>
            <p className="mb-2">
              <strong>Purpose:</strong> Payment processing and fraud prevention
            </p>
            <p className="mb-2">
              <strong>Provider:</strong> Stripe, Inc.
            </p>
            <p className="mb-4">
              <strong>Duration:</strong> Varies (session to 1 year)
            </p>
            <p className="text-sm text-muted-foreground">
              Stripe uses cookies during the payment and checkout process to
              prevent fraud and ensure secure transactions. These cookies are
              set when you interact with Stripe's payment forms. For more
              information, see{" "}
              <a
                href="https://stripe.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-foreground hover:underline"
              >
                Stripe's Privacy Policy
              </a>
              .
            </p>
          </div>

          <div className="bg-muted/50 border border-border rounded-2xl p-6">
            <p className="mb-4">
              <strong>Cloudflare Cookies</strong>
            </p>
            <p className="mb-2">
              <strong>Purpose:</strong> Security, performance, and DDoS
              protection
            </p>
            <p className="mb-2">
              <strong>Provider:</strong> Cloudflare, Inc.
            </p>
            <p className="mb-4">
              <strong>Duration:</strong> Session-based
            </p>
            <p className="text-sm text-muted-foreground">
              Cloudflare may set cookies for security purposes, such as
              detecting malicious visitors and minimizing blocking legitimate
              users. For more information, see{" "}
              <a
                href="https://www.cloudflare.com/privacypolicy/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-foreground hover:underline"
              >
                Cloudflare's Privacy Policy
              </a>
              .
            </p>
          </div>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">
            4. Cookies We Don't Use
          </h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            We currently do <strong>NOT</strong> use:
          </p>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
            <li>Analytics or tracking cookies (e.g., Google Analytics)</li>
            <li>Advertising or marketing cookies</li>
            <li>Social media cookies</li>
            <li>Cross-site tracking cookies</li>
          </ul>
          <p className="text-muted-foreground leading-relaxed mt-4">
            If we introduce analytics or marketing cookies in the future, we
            will update this policy and obtain your consent where required by
            law.
          </p>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">5. Cookie Consent</h2>

          <h3 className="text-xl font-semibold mb-3 mt-6">
            5.1 Essential Cookies
          </h3>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Essential cookies (authentication and security) do not require your
            consent under GDPR Article 6(1)(b) and ePrivacy Directive Article
            5(3), as they are strictly necessary for the service you have
            requested.
          </p>

          <h3 className="text-xl font-semibold mb-3 mt-6">
            5.2 Optional Cookies
          </h3>
          <p className="text-muted-foreground leading-relaxed">
            We currently do not use optional cookies (analytics, marketing). If
            we introduce them in the future, we will:
          </p>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4 mt-4">
            <li>
              Obtain your explicit consent before setting non-essential cookies
            </li>
            <li>
              Provide granular controls to accept or reject specific cookie
              categories
            </li>
            <li>Allow you to withdraw consent at any time</li>
            <li>Not use pre-ticked boxes or implied consent</li>
          </ul>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">
            6. Managing and Deleting Cookies
          </h2>

          <h3 className="text-xl font-semibold mb-3 mt-6">
            6.1 Browser Settings
          </h3>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Most web browsers allow you to manage cookies through your browser
            settings. You can:
          </p>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
            <li>View what cookies are stored and delete them individually</li>
            <li>Block third-party cookies</li>
            <li>Block all cookies from specific websites</li>
            <li>Clear all cookies when you close your browser</li>
            <li>Delete all cookies currently stored</li>
          </ul>

          <p className="text-muted-foreground leading-relaxed mt-6 mb-4">
            Here are links to cookie management instructions for popular
            browsers:
          </p>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
            <li>
              <a
                href="https://support.google.com/chrome/answer/95647"
                target="_blank"
                rel="noopener noreferrer"
                className="text-foreground hover:underline"
              >
                Google Chrome
              </a>
            </li>
            <li>
              <a
                href="https://support.mozilla.org/en-US/kb/enhanced-tracking-protection-firefox-desktop"
                target="_blank"
                rel="noopener noreferrer"
                className="text-foreground hover:underline"
              >
                Mozilla Firefox
              </a>
            </li>
            <li>
              <a
                href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac"
                target="_blank"
                rel="noopener noreferrer"
                className="text-foreground hover:underline"
              >
                Safari
              </a>
            </li>
            <li>
              <a
                href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09"
                target="_blank"
                rel="noopener noreferrer"
                className="text-foreground hover:underline"
              >
                Microsoft Edge
              </a>
            </li>
          </ul>

          <h3 className="text-xl font-semibold mb-3 mt-6">
            6.2 Impact of Blocking Cookies
          </h3>
          <div className="bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800 rounded-2xl p-6">
            <p className="text-muted-foreground leading-relaxed mb-4">
              <strong>Important:</strong> If you block or delete essential
              cookies, you will not be able to use our service. Specifically:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
              <li>You will not be able to log in or stay logged in</li>
              <li>Your session will not persist between page loads</li>
              <li>Authentication-protected features will be inaccessible</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">7. More Information</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            For more information about how we process your personal data, please
            see our{" "}
            <Link to="/privacy" className="text-foreground hover:underline">
              Privacy Policy
            </Link>
            .
          </p>
          <p className="text-muted-foreground leading-relaxed">
            To learn more about cookies in general, visit{" "}
            <a
              href="https://www.allaboutcookies.org"
              target="_blank"
              rel="noopener noreferrer"
              className="text-foreground hover:underline"
            >
              www.allaboutcookies.org
            </a>
            .
          </p>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">
            8. Changes to This Cookie Policy
          </h2>
          <p className="text-muted-foreground leading-relaxed">
            We may update this Cookie Policy from time to time to reflect
            changes in our use of cookies or legal requirements. The "Last
            Updated" date at the top indicates when this policy was last
            revised. We encourage you to review this policy periodically.
          </p>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">9. Contact Us</h2>
          <div className="bg-muted/50 border border-border rounded-2xl p-6">
            <p className="mb-4">
              If you have questions about our use of cookies, please contact us:
            </p>
            <p>
              <strong>Email:</strong>{" "}
              <a
                href="mailto:hey@harshil.dev"
                className="text-foreground hover:underline"
              >
                hey@harshil.dev
              </a>
            </p>
          </div>
        </div>
      </section>
    </LegalLayout>
  );
}
